"use client";

import { useId, useState } from "react";
import type { FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { useCaptcha, useLogin } from "@/features/auth/useAuth";
import type { LoginRequest } from "@/features/auth/types";

/**
 * 登录页：用户名/手机号 + 密码；图形验证码由后端配置决定是否显示和校验。
 * 成功后按 ?redirect= 返回原页（默认首页），用硬跳转让会话 Cookie 生效、中间件重新鉴权。
 * 样式见 globals.css 的 .login-* 一组类（沿用明/暗主题 token）。
 */
export default function LoginPage() {
  const captcha = useCaptcha();
  const login = useLogin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaCode, setCaptchaCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const usernameId = useId();
  const passwordId = useId();
  const captchaId = useId();
  const captchaEnabled = captcha.data?.enabled === true;

  function refreshCaptcha() {
    setCaptchaCode("");
    captcha.refetch();
  }

  function mapError(e: unknown): string {
    if (e instanceof ApiError) {
      switch (e.code) {
        case "CAPTCHA_INVALID":
          return "验证码错误或已过期，请重试";
        case "INVALID_CREDENTIALS":
          return "用户名或密码错误";
        case "VALIDATION_FAILED":
          return captchaEnabled ? "请完整填写用户名、密码与验证码" : "请完整填写用户名和密码";
        default:
          return e.message || "登录失败";
      }
    }
    return "登录失败，请重试";
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!captcha.data) {
      setError("登录配置未加载，请重试");
      return;
    }
    const captchaKey = captcha.data.captcha_id;
    if (captchaEnabled && (!captchaKey || !captchaCode.trim())) {
      setError("请输入图形验证码");
      return;
    }
    const req: LoginRequest = {
      username: username.trim(),
      password,
      ...(captchaEnabled && captchaKey
        ? { captcha_id: captchaKey, captcha_code: captchaCode.trim() }
        : {}),
    };
    login.mutate(req, {
      onSuccess: () => {
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get("redirect") || "/";
        window.location.assign(redirect);
      },
      onError: (e) => {
        setError(mapError(e));
        // 验证码一次性，启用时失败后必刷新。
        if (captchaEnabled) refreshCaptcha();
      },
    });
  }

  if (process.env.NODE_ENV === "development" && captcha.data?.read_only_preview === true) {
    return (
      <div className="login-wrap">
        <section className="login-card" aria-labelledby="preview-title">
          <div className="login-head">
            <h1 id="preview-title" className="login-brand">摘要阅读 · 只读预览</h1>
            <p className="login-sub">当前是本地排版预览，无需管理员账号或密码。</p>
          </div>
          <p className="mb-6 text-sm leading-7 text-muted">展示线上情报的只读快照，可切换股票、筛选来源和展开摘要。此入口不提供后台管理或数据修改功能。</p>
          <a href="/api/v1/preview/start" className="login-submit block text-center no-underline">进入只读预览</a>
        </section>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <form onSubmit={onSubmit} className="login-card" noValidate>
        <div className="login-head">
          <div className="login-brand">驾驭系统</div>
          <div className="login-sub">登录以继续</div>
        </div>

        <div className="login-field">
          <label className="login-label" htmlFor={usernameId}>
            用户名或手机号
          </label>
          <input
            id={usernameId}
            className="login-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="请输入用户名或手机号"
            autoComplete="username"
            autoFocus
          />
        </div>

        <div className="login-field">
          <label className="login-label" htmlFor={passwordId}>
            密码
          </label>
          <div className="login-pwd">
            <input
              id={passwordId}
              className="login-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入密码"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="login-eye"
              onClick={() => setShowPassword((v) => !v)}
              title={showPassword ? "隐藏密码" : "显示密码"}
              aria-label={showPassword ? "隐藏密码" : "显示密码"}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>

        {captchaEnabled ? (
          <div className="login-field">
            <label className="login-label" htmlFor={captchaId}>
              验证码
            </label>
            <div className="login-captcha">
              <input
                id={captchaId}
                className="login-input"
                value={captchaCode}
                onChange={(e) => setCaptchaCode(e.target.value)}
                placeholder="输入图中字符"
                autoComplete="off"
              />
              <button
                type="button"
                className="login-captcha-img"
                onClick={refreshCaptcha}
                title="点击刷新验证码"
                aria-label="刷新验证码"
              >
                {captcha.data?.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={captcha.data.image} alt="图形验证码" />
                ) : (
                  <span className="ph">点击加载</span>
                )}
              </button>
            </div>
          </div>
        ) : null}

        {captcha.isError ? (
          <div className="login-error" role="alert" aria-live="polite">
            登录配置加载失败，请刷新页面重试
          </div>
        ) : null}

        {error ? (
          <div className="login-error" role="alert" aria-live="polite">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          className="login-submit"
          disabled={login.isPending || captcha.isLoading || captcha.isError}
        >
          {login.isPending ? "登录中…" : captcha.isLoading ? "加载中…" : "登录"}
        </button>
      </form>
    </div>
  );
}

/** 眼睛（明文）图标，Lucide 风格、统一 2px 描边。 */
function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** 眼睛带斜杠（隐藏）图标。 */
function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.4" />
      <path d="M6.6 6.6A13.2 13.2 0 0 0 2 11s3.5 7 10 7a9.1 9.1 0 0 0 5.4-1.6" />
      <path d="m2 2 20 20" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}
